import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Cctv, Loader2 } from 'lucide-react';

const SuperAdminCameras = () => {
    const [globalCameras, setGlobalCameras] = useState([]);
    const [camerasLoading, setCamerasLoading] = useState(true);
    const [testingCameras, setTestingCameras] = useState({});
    const [cameraTestResults, setCameraTestResults] = useState({});

    const headers = {
        Authorization: `Bearer ${localStorage.getItem('token')}`
    };

    const fetchGlobalCameras = async () => {
        setCamerasLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('http://localhost:4000/api/cameras', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setGlobalCameras(response.data);
        } catch (err) {
            console.error("Error loading global cameras:", err);
        } finally {
            setCamerasLoading(false);
        }
    };

    useEffect(() => {
        fetchGlobalCameras();
    }, []);

    const handleTestCamera = async (camId, rtspUrl, projectId) => {
        setTestingCameras(prev => ({ ...prev, [camId]: true }));
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post('http://localhost:4000/api/cameras/test-connection', {
                rtsp_url: rtspUrl
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setCameraTestResults(prev => ({ ...prev, [camId]: res.data }));
            
            const newStatus = res.data.success ? 'active' : 'offline';
            await axios.put(`http://localhost:4000/api/cameras/${camId}`, {
                status: newStatus
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-project-id': projectId
                }
            });
            
            fetchGlobalCameras();
        } catch (err) {
            console.error("Error testing camera:", err);
            setCameraTestResults(prev => ({ 
                ...prev, 
                [camId]: { success: false, message: "Error al conectar." } 
            }));
        } finally {
            setTestingCameras(prev => ({ ...prev, [camId]: false }));
        }
    };

    const formatBytes = (bytes) => {
        const val = parseInt(bytes || 0, 10);
        if (val === 0) return '0 B';
        if (val < 1024 * 1024) return `${(val / 1024).toFixed(1)} KB`;
        if (val < 1024 * 1024 * 1024) return `${(val / 1024 / 1024).toFixed(1)} MB`;
        return `${(val / 1024 / 1024 / 1024).toFixed(2)} GB`;
    };

    if (camerasLoading) {
        return (
            <div className="min-h-screen bg-theme-bg flex items-center justify-center">
                <div className="relative">
                    <div className="w-16 h-16 border-4 border-purple-500/20 rounded-full"></div>
                    <div className="w-16 h-16 border-4 border-purple-500 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-theme-bg text-theme-text-primary p-4 md:p-8 font-sans pb-20">
            <div className="max-w-7xl mx-auto space-y-6">
                
                {/* Header */}
                <div className="border-b border-theme-border/80 pb-6">
                    <h1 className="text-3xl font-extrabold tracking-tight text-theme-text-primary flex items-center gap-3">
                        <Cctv className="text-purple-500" size={32} /> Monitoreo de Cámaras
                    </h1>
                    <p className="text-theme-text-secondary text-xs mt-1">Supervisión global de nodos de videovigilancia IoT y prueba de transmisión en vivo.</p>
                </div>

                <div className="bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl p-6 relative overflow-hidden shadow-xl">
                    <h3 className="text-lg font-bold text-theme-text-primary mb-2 flex items-center gap-2">
                        <Cctv size={20} className="text-purple-450" /> Estatus de Cámaras por Proyecto
                    </h3>
                    <p className="text-xs text-theme-text-secondary mb-6">
                        Consola global de supervisión de red de videovigilancia. Ejecuta pruebas de conexión en vivo y verifica la disponibilidad de los nodos de captura.
                    </p>

                    {globalCameras.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                            <div className="bg-theme-bg/30 border border-theme-border/50 rounded-2xl p-4 flex justify-between items-center">
                                <div>
                                    <span className="text-[10px] uppercase tracking-wider text-theme-text-secondary font-bold">Total Cámaras Activas</span>
                                    <h4 className="text-2xl font-black text-theme-text-primary mt-1">
                                        {globalCameras.filter(c => c.status !== 'offline').length} <span className="text-xs text-theme-text-secondary">/ {globalCameras.length}</span>
                                    </h4>
                                </div>
                            </div>
                            <div className="bg-theme-bg/30 border border-theme-border/50 rounded-2xl p-4 flex justify-between items-center">
                                <div>
                                    <span className="text-[10px] uppercase tracking-wider text-theme-text-secondary font-bold">Ancho de Banda Acumulado (Celular/Red)</span>
                                    <h4 className="text-2xl font-black text-purple-400 mt-1">
                                        {formatBytes(globalCameras.reduce((acc, c) => acc + parseInt(c.data_consumed_bytes || 0, 10), 0))}
                                    </h4>
                                </div>
                            </div>
                        </div>
                    )}

                    {globalCameras.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-theme-text-secondary border border-dashed border-theme-border rounded-2xl">
                            <Cctv size={48} className="opacity-30 mb-2" />
                            <span className="text-sm font-bold">No hay cámaras registradas en el sistema.</span>
                            <span className="text-xs text-theme-text-secondary mt-1">Las empresas e ingenieros aún no han añadido nodos de CCTV.</span>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {globalCameras.map(cam => {
                                const testRes = cameraTestResults[cam.id];
                                const isTesting = testingCameras[cam.id];
                                
                                return (
                                    <div key={cam.id} className="bg-theme-bg/50 border border-theme-border rounded-3xl p-5 shadow-md flex flex-col justify-between hover:border-purple-500/30 transition-all duration-300 relative overflow-hidden group">
                                        <div>
                                            {/* Header: Cliente y Obra */}
                                            <div className="flex justify-between items-start mb-3 border-b border-theme-border/30 pb-3">
                                                <div className="overflow-hidden">
                                                    <span className="text-[9px] font-black uppercase text-purple-400 block tracking-widest truncate">{cam.company_name || 'Compañía Desconocida'}</span>
                                                    <strong className="text-xs text-theme-text-primary tracking-tight block truncate mt-0.5">{cam.project_name || 'Obra Desconocida'}</strong>
                                                </div>
                                                <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase border tracking-wider shrink-0 flex items-center gap-1.5 ${
                                                    cam.status === 'offline' 
                                                        ? 'bg-rose-500/10 border-rose-500/20 text-rose-400' 
                                                        : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-405'
                                                }`}>
                                                    <span className={`h-1.5 w-1.5 rounded-full ${cam.status === 'offline' ? 'bg-rose-500' : 'bg-emerald-400 animate-pulse'}`}></span>
                                                    {cam.status === 'offline' ? 'Offline' : 'Online'}
                                                </span>
                                            </div>

                                            {/* Info de la Cámara */}
                                            <div className="space-y-2 mt-2">
                                                <div className="flex justify-between items-center">
                                                    <h4 className="font-bold text-sm text-theme-text-primary truncate">{cam.name}</h4>
                                                    <span className="text-[9px] font-mono text-theme-text-secondary bg-theme-bg border border-theme-border px-1.5 py-0.5 rounded">ID: #{cam.id}</span>
                                                </div>
                                                <p className="text-xs text-theme-text-secondary line-clamp-1">{cam.location_description || 'Sin ubicación específica'}</p>
                                                <div className="bg-theme-bg/60 border border-theme-border/60 rounded-xl p-2 font-mono text-[9px] text-zinc-500 break-all select-all flex items-center justify-between">
                                                    <span className="truncate mr-1">{cam.rtsp_url}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-[10px] text-theme-text-secondary mt-1">
                                                     <span>Datos Consumidos:</span>
                                                     <span className="font-bold text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">
                                                         {formatBytes(cam.data_consumed_bytes)}
                                                     </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Footer Acciones */}
                                        <div className="mt-5 pt-3 border-t border-theme-border/30">
                                            {testRes && (
                                                <div className={`mb-3 p-2.5 rounded-xl text-[10px] leading-relaxed border ${
                                                    testRes.success 
                                                        ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' 
                                                        : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
                                                }`}>
                                                    <strong>{testRes.success ? 'Conexión OK' : 'Fallo de Conexión'}:</strong> {testRes.message}
                                                </div>
                                            )}

                                            <button
                                                disabled={isTesting}
                                                onClick={() => handleTestCamera(cam.id, cam.rtsp_url, cam.project_id)}
                                                className="w-full bg-theme-bg hover:bg-theme-border/30 border border-theme-border py-2 text-theme-text-primary rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                                            >
                                                {isTesting ? (
                                                    <>
                                                        <Loader2 size={12} className="animate-spin text-purple-400" />
                                                        Probando Conexión...
                                                    </>
                                                ) : (
                                                    <>Probar Conexión en Vivo</>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SuperAdminCameras;
